import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaClipboardCheck, FaCalendarAlt, FaBuilding, FaHistory, FaClock, FaCheckCircle } from "react-icons/fa";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { useBuilding } from "../context/BuildingContext";
import { tolerantId } from "../utils/firestoreHelpers";
import { formatDateLocalized } from "../utils/formatters";
import EmptyStateComingSoon from "../components/EmptyStateComingSoon";
import { useIsMobile } from "../hooks/useResponsive";
import {
  AppScreen,
  AppHeader,
  AppCard,
  AppEmptyState,
  AppSpinner,
  SectionLabel,
  Badge,
  IconBox,
} from "../components/app";
import { appColors, appType, appSpacing, appRadius } from "../utils/appTheme";

const Revisiones = ({ setSelected, goBack }) => {
  const { t } = useTranslation("revisiones");
  const { selectedBuilding } = useBuilding();
  const isMobile = useIsMobile();
  const [proximas, setProximas] = useState([]);
  const [pasadas, setPasadas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRevisiones = async () => {
      if (!selectedBuilding?.buildingid) {
        setLoading(false);
        return;
      }
      try {
        const q = query(
          collection(db, "inspections"),
          where("buildingid", "in", tolerantId(selectedBuilding.buildingid))
        );
        const snap = await getDocs(q);
        const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const now = new Date();

        const futuras = data.filter((r) => {
          const fecha = r.date?.seconds ? new Date(r.date.seconds * 1000) : new Date(r.date);
          return fecha >= now;
        }).sort((a, b) => {
          const da = a.date?.seconds ? a.date.seconds : new Date(a.date).getTime() / 1000;
          const db2 = b.date?.seconds ? b.date.seconds : new Date(b.date).getTime() / 1000;
          return da - db2;
        });

        const anteriores = data.filter((r) => {
          const fecha = r.date?.seconds ? new Date(r.date.seconds * 1000) : new Date(r.date);
          return fecha < now;
        }).sort((a, b) => {
          const da = a.date?.seconds ? a.date.seconds : new Date(a.date).getTime() / 1000;
          const db2 = b.date?.seconds ? b.date.seconds : new Date(b.date).getTime() / 1000;
          return db2 - da;
        });

        setProximas(futuras);
        setPasadas(anteriores);
      } catch (error) {
        console.error("Error al cargar revisiones:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchRevisiones();
  }, [selectedBuilding]);

  const formatDate = (date) => {
    if (!date) return "";
    const d = date?.seconds ? new Date(date.seconds * 1000) : new Date(date);
    return formatDateLocalized(d);
  };

  const noData = proximas.length === 0 && pasadas.length === 0;

  // ─── Versión mobile: estilo app "Radial" ────────────────────────────────────
  if (isMobile) {
    // Tarjeta rica de revisión (IconBox clipboard + micro-labels + footer en grilla
    // + línea de estado coloreada abajo). `tone` = color de estado (frío/verde).
    const RevisionCard = ({ rev, tone, badge }) => {
      const empresa = rev.company || rev.empresa || t("noCompany");
      return (
        <AppCard padding={0} style={{ overflow: "hidden" }}>
          {/* Cabecera: icono clipboard + micro-label + título + badge de estado */}
          <div style={{ padding: "16px 16px 0", display: "flex", alignItems: "flex-start", gap: 12 }}>
            <IconBox icon={<FaClipboardCheck />} color={tone} size={40} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ ...appType.statLabel, color: tone, marginBottom: 3 }}>{t("inspection")}</div>
              <div style={{ ...appType.cardTitle, fontSize: 15, overflowWrap: "anywhere" }}>
                {rev.title || rev.titulo || t("noTitle")}
              </div>
            </div>
            <Badge tone={badge.tone} dot style={{ flexShrink: 0 }}>{badge.label}</Badge>
          </div>

          {/* Footer en grilla: EMPRESA / FECHA */}
          <div style={{ padding: "14px 16px 16px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ ...appType.statLabel, color: appColors.ink3, marginBottom: 4, display: "flex", alignItems: "center", gap: 5 }}>
                <FaBuilding size={9} /> {t("fields.company")}
              </div>
              <div style={{ ...appType.body, fontSize: 13.5, overflowWrap: "anywhere" }}>
                {empresa}
              </div>
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ ...appType.statLabel, color: appColors.ink3, marginBottom: 4, display: "flex", alignItems: "center", gap: 5 }}>
                <FaCalendarAlt size={9} /> {t("fields.date")}
              </div>
              <div style={{ ...appType.body, fontSize: 13.5, fontVariantNumeric: "tabular-nums" }}>
                {formatDate(rev.date) || "—"}
              </div>
            </div>
          </div>

          {/* Línea de estado coloreada abajo */}
          <div style={{ height: 3, background: tone, borderRadius: appRadius.pill }} />
        </AppCard>
      );
    };

    return (
      <AppScreen
        header={
          <AppHeader
            title={t("title")}
            subtitle={selectedBuilding?.name || t("subtitle")}
            onBack={goBack}
          />
        }
      >
        {loading ? (
          <AppSpinner />
        ) : noData ? (
          <AppEmptyState
            icon={<FaClipboardCheck />}
            title={t("empty.title")}
            description={t("empty.description")}
          />
        ) : (
          <>
            {proximas.length > 0 && (
              <>
                <SectionLabel style={{ marginBottom: 12 }}>{t("sections.upcoming")}</SectionLabel>
                <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: appSpacing.section }}>
                  {proximas.map((rev) => (
                    <RevisionCard
                      key={rev.id}
                      rev={rev}
                      tone={appColors.amber}
                      badge={{ tone: "amber", label: t("status.scheduled") }}
                    />
                  ))}
                </div>
              </>
            )}

            {pasadas.length > 0 && (
              <>
                <SectionLabel style={{ marginBottom: 12 }}>{t("sections.past")}</SectionLabel>
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {pasadas.map((rev) => (
                    <RevisionCard
                      key={rev.id}
                      rev={rev}
                      tone={appColors.green}
                      badge={{ tone: "green", label: t("status.done") }}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </AppScreen>
    );
  }

  // ─── Versión desktop (existente) ─────────────────────────────────────────────
  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <div style={styles.headerIcon}><FaClipboardCheck /></div>
          <div style={styles.headerInfo}>
            <h1 style={styles.title}>{t("title")}</h1>
            <p style={styles.subtitle}>{t("subtitleDesktop")}</p>
          </div>
        </div>
        <p style={{ textAlign: "center", color: "#6b7280", padding: "40px" }}>{t("loading")}</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerIcon}><FaClipboardCheck /></div>
        <div style={styles.headerInfo}>
          <h1 style={styles.title}>{t("title")}</h1>
          <p style={styles.subtitle}>{t("subtitleDesktop")}</p>
        </div>
      </div>

      {noData ? (
        <EmptyStateComingSoon
          icon={<FaClipboardCheck />}
          title={t("empty.title")}
          description={t("empty.descriptionDesktop")}
        />
      ) : (
        <>
          {proximas.length > 0 && (
            <>
              <div style={styles.sectionHeader}>
                <FaClock style={styles.sectionIcon} />
                <h2 style={styles.sectionTitle}>{t("sections.upcomingDesktop")}</h2>
              </div>
              <div style={styles.grid}>
                {proximas.map((rev) => (
                  <div key={rev.id} style={styles.card}>
                    <h3 style={styles.cardTitle}>{rev.title || rev.titulo}</h3>
                    <div style={styles.detailRow}>
                      <FaBuilding style={styles.detailIcon} />
                      <span style={styles.detailText}>{rev.company || rev.empresa || t("noCompany")}</span>
                    </div>
                    <div style={styles.detailRow}>
                      <FaCalendarAlt style={styles.detailIcon} />
                      <span style={styles.detailText}>{formatDate(rev.date)}</span>
                    </div>
                    <div style={styles.statusPending}>
                      <FaClock style={styles.statusIcon} />
                      {t("status.scheduled")}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {pasadas.length > 0 && (
            <>
              <div style={styles.sectionHeader}>
                <FaHistory style={styles.sectionIcon} />
                <h3 style={styles.sectionTitle}>{t("sections.pastDesktop")}</h3>
              </div>
              <div style={styles.gridSecondary}>
                {pasadas.map((rev) => (
                  <div key={rev.id} style={styles.cardSecondary}>
                    <div style={styles.cardSecondaryTop}>
                      <h4 style={styles.cardTitleSecondary}>{rev.title || rev.titulo}</h4>
                      <span style={styles.statusCompleted}><FaCheckCircle /></span>
                    </div>
                    <div style={styles.detailRowSecondary}>
                      <FaBuilding style={styles.detailIconSecondary} />
                      <span style={styles.detailTextSecondary}>{rev.company || rev.empresa || t("noCompany")}</span>
                    </div>
                    <div style={styles.detailRowSecondary}>
                      <FaCalendarAlt style={styles.detailIconSecondary} />
                      <span style={styles.detailTextSecondary}>{formatDate(rev.date)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

const styles = {
  container: { padding: "24px", fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif", backgroundColor: "#f5f7fa", minHeight: "100vh" },
  header: { background: "white", borderRadius: "12px", padding: "16px 20px", marginBottom: "20px", boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)", border: "1px solid #e5e7eb", display: "flex", alignItems: "center", gap: "14px" },
  headerIcon: { width: "42px", height: "42px", borderRadius: "10px", backgroundColor: "#0080ff", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px" },
  headerInfo: { flex: 1 },
  title: { fontSize: "20px", fontWeight: 400, margin: 0, color: "#0080ff" },
  subtitle: { fontSize: "13px", color: "#6b7280", margin: "2px 0 0 0", fontWeight: 400 },
  sectionHeader: { display: "flex", alignItems: "center", gap: "10px", marginBottom: "16px", marginTop: "32px" },
  sectionIcon: { color: "#0080ff", fontSize: "18px" },
  sectionTitle: { fontSize: "18px", margin: 0, color: "#1a1a1a", fontWeight: 400 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "20px" },
  gridSecondary: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "16px" },
  card: { backgroundColor: "#fff", border: "1px solid #e5e7eb", borderRadius: "12px", padding: "20px", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" },
  cardTitle: { fontSize: "16px", fontWeight: 400, marginBottom: "14px", color: "#1a1a1a", margin: "0 0 14px 0" },
  detailRow: { display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" },
  detailIcon: { color: "#0080ff", fontSize: "14px", width: "16px" },
  detailText: { fontSize: "14px", color: "#4b5563" },
  statusPending: { display: "inline-flex", alignItems: "center", gap: "6px", marginTop: "12px", padding: "6px 12px", backgroundColor: "#e8f4ff", color: "#0080ff", borderRadius: "6px", fontSize: "12px", fontWeight: 400 },
  statusIcon: { fontSize: "12px" },
  cardSecondary: { backgroundColor: "#fff", border: "1px solid #e5e7eb", borderRadius: "10px", padding: "16px 18px" },
  cardSecondaryTop: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "12px" },
  cardTitleSecondary: { fontSize: "15px", fontWeight: 400, margin: 0, color: "#1a1a1a", flex: 1 },
  statusCompleted: { color: "#16a34a", fontSize: "16px" },
  detailRowSecondary: { display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" },
  detailIconSecondary: { color: "#9ca3af", fontSize: "12px", width: "14px" },
  detailTextSecondary: { fontSize: "13px", color: "#6b7280" },
};

export default Revisiones;
